/**
 * The design's four colour accents, each measured rather than copied.
 *
 * The design ships `emerald`, `cyber`, `amber` and `mint` as a pair of raw
 * hexes apiece and paints text, fills and chart bars with them directly. Three
 * of the four are unreadable that way in light mode -- `#00F5D4` on a white
 * card is 1.3:1 and `#FBBF24` is 1.6:1, against the 4.5:1 section 4 asks of
 * text and the 3:1 WCAG 1.4.11 asks of a graphic that carries meaning.
 *
 * So each preset declares seven tokens per mode instead of two, on the same
 * split the default palette already uses: `accent` fills and draws, `accentInk`
 * is what accent-coloured *text* uses, `btn`/`btnInk` are measured as a pair
 * for filled controls. `swatch` is the design's own hex, used only for the
 * dot in the picker, where it is decoration beside a written label.
 *
 * Every ratio in here is asserted by `tests/accents.test.ts`, not trusted.
 */

export interface AccentTokens {
  accent: string;
  accentInk: string;
  accentGlow: string;
  accent2: string;
  accent2Ink: string;
  btn: string;
  btnInk: string;
}

export interface AccentPreset {
  id: string;
  label: string;
  /** The design's own two hexes, for the picker's swatch. */
  primary: string;
  secondary: string;
  light: AccentTokens;
  dark: AccentTokens;
}

export const ACCENT_PRESETS = [
  {
    id: "emerald",
    label: "Emerald Solar",
    primary: "#E5F145",
    secondary: "#25C16F",
    // The default palette, repeated here so switching away and back restores
    // exactly what the stylesheet declares rather than something near it.
    light: {
      accent: "#818d11",
      accentInk: "#656f0d",
      accentGlow: "rgba(129, 141, 17, 0.18)",
      accent2: "#16a34a",
      accent2Ink: "#12823b",
      btn: "#656f0d",
      btnInk: "#ffffff",
    },
    dark: {
      accent: "#e5f145",
      accentInk: "#e5f145",
      accentGlow: "rgba(229, 241, 69, 0.35)",
      accent2: "#25c16f",
      accent2Ink: "#25c16f",
      btn: "#e5f145",
      btnInk: "#060a08",
    },
  },
  {
    id: "cyber",
    label: "Cyber Pulse",
    primary: "#00F5D4",
    secondary: "#7B2CBF",
    light: {
      accent: "#0d9488",
      accentInk: "#0f766e",
      accentGlow: "rgba(13, 148, 136, 0.18)",
      accent2: "#7c3aed",
      accent2Ink: "#6d28d9",
      btn: "#0f766e",
      btnInk: "#ffffff",
    },
    dark: {
      accent: "#00f5d4",
      accentInk: "#00f5d4",
      accentGlow: "rgba(0, 245, 212, 0.30)",
      accent2: "#c084fc",
      accent2Ink: "#c084fc",
      btn: "#00f5d4",
      btnInk: "#060a08",
    },
  },
  {
    id: "amber",
    label: "Solar Amber",
    primary: "#FBBF24",
    secondary: "#F97316",
    light: {
      accent: "#b45309",
      accentInk: "#92400e",
      accentGlow: "rgba(180, 83, 9, 0.16)",
      accent2: "#c2410c",
      accent2Ink: "#9a3412",
      btn: "#92400e",
      btnInk: "#ffffff",
    },
    dark: {
      accent: "#fbbf24",
      accentInk: "#fbbf24",
      accentGlow: "rgba(251, 191, 36, 0.30)",
      accent2: "#fb923c",
      accent2Ink: "#fb923c",
      btn: "#fbbf24",
      btnInk: "#060a08",
    },
  },
  {
    id: "mint",
    label: "Arctic Mint",
    primary: "#38BDF8",
    secondary: "#10B981",
    light: {
      accent: "#0284c7",
      accentInk: "#0369a1",
      accentGlow: "rgba(2, 132, 199, 0.16)",
      accent2: "#059669",
      accent2Ink: "#047857",
      btn: "#0369a1",
      btnInk: "#ffffff",
    },
    dark: {
      accent: "#38bdf8",
      accentInk: "#38bdf8",
      accentGlow: "rgba(56, 189, 248, 0.30)",
      accent2: "#34d399",
      accent2Ink: "#34d399",
      btn: "#38bdf8",
      btnInk: "#060a08",
    },
  },
] as const satisfies readonly AccentPreset[];

/** The card and page each mode is measured against, from `styles.css`. */
export const SURFACES = {
  light: { card: "#ffffff", page: "#f4f7f5" },
  dark: { card: "#0e1612", page: "#060a08" },
} as const;

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance, WCAG 2.1 formula. Opaque `#rrggbb` only. */
export function luminance(hex: string): number {
  const value = Number.parseInt(hex.replace("#", ""), 16);
  const r = channel((value >> 16) & 0xff);
  const g = channel((value >> 8) & 0xff);
  const b = channel(value & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const one = luminance(a);
  const two = luminance(b);
  return (Math.max(one, two) + 0.05) / (Math.min(one, two) + 0.05);
}

/** The CSS custom properties a preset sets, for one resolved mode. */
export function variablesFor(preset: AccentPreset, mode: "light" | "dark"): Record<string, string> {
  const tokens = mode === "dark" ? preset.dark : preset.light;
  return {
    "--accent": tokens.accent,
    "--accent-ink": tokens.accentInk,
    "--accent-glow": tokens.accentGlow,
    "--accent-2": tokens.accent2,
    "--accent-2-ink": tokens.accent2Ink,
    "--btn": tokens.btn,
    "--btn-ink": tokens.btnInk,
  };
}
